/**
 * Contexts — generic show view (the `/contexts/:id/details` tooling route).
 *
 * The primary `/contexts/:id` route is the rich {@link ContextDetail}; this is
 * the plain Inspector-grammar show kept reachable for tooling/tests, built on
 * the shared `ShowPage<T>` template (design-system §4.3). It leads with the
 * meaningful fields (type, maturity, owner), renders the description through
 * `<Markdown>`, and demotes ids / timestamps to the muted Technical tail.
 */
import { Pencil } from "@/components/ui/icon";
import type { Context } from "@aos/shared";

import { useGo, useNavigation } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Field, RefLink, ShowPage } from "@/components/inspector";
import { Markdown } from "@/components/app";
import { MaturityBadge } from "./MaturityBadge";
import { AdvanceMaturityAction } from "./AdvanceMaturityAction";
import { humanize } from "@/lib/format";
import { formatDateTime } from "../_shared/runs-ui";

export function ContextShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  // This show lives at the non-standard `/contexts/:id/details` route, so
  // Refine's `useShow` cannot infer the id from the URL (it expects
  // `/contexts/:id`). Read it from the route param and pass it explicitly,
  // otherwise the page is stuck on its loading skeleton forever.
  const { id } = useParams<{ id: string }>();

  return (
    <ShowPage<Context>
      resource="contexts"
      id={id}
      title={(c) => c.name}
      subtitle="An operating context — its fields, workflows, principles, and bound entities."
      status={(c) => c.status}
      primaryAction={(c) => (
        <AdvanceMaturityAction contextId={c.id} stage={c.maturity_stage} />
      )}
      actions={(c) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => go({ to: editUrl("contexts", c.id) })}
        >
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </Button>
      )}
      overview={(c) => (
        <>
          <Field label="Type">{humanize(c.context_type)}</Field>
          <Field label="Maturity">
            <MaturityBadge stage={c.maturity_stage} showOrdinal />
          </Field>
          <Field label="Priority">{c.priority}</Field>
          <Field label="Confidentiality">{humanize(c.confidentiality)}</Field>
          <Field label="Host">
            {c.host_type ? humanize(c.host_type) : "—"}
          </Field>
          <Field label="Owner">
            <RefLink resource="users" id={c.owner_user_id} />
          </Field>
          <Field label="Boundary" full>
            {c.boundary ? (
              <span className="whitespace-pre-wrap">{c.boundary}</span>
            ) : (
              "—"
            )}
          </Field>
        </>
      )}
      body={(c) =>
        c.description && c.description.trim() ? (
          <Markdown source={c.description} />
        ) : null
      }
      technical={(c) => (
        <>
          <Field label="Host id">
            <RefLink resource="artifacts" id={c.host_id} resolve={false} />
          </Field>
          <Field label="Review at">{formatDateTime(c.review_at)}</Field>
          <Field label="Created">{formatDateTime(c.created_at)}</Field>
          <Field label="Updated">{formatDateTime(c.updated_at)}</Field>
        </>
      )}
    />
  );
}

export default ContextShow;
